import React, { useState } from 'react';
import { HelpCircle, CheckCircle, XCircle, RotateCcw, ThumbsUp, ThumbsDown } from 'lucide-react';

interface QuestionOption {
  answertext: string;
  isCorrect: boolean;
  explanation: string;
}

interface QuizQuestion {
  questionText: string;
  options: QuestionOption[];
}

interface QuizPlayerProps {
  questions: QuizQuestion[];
  title: string;
  description?: string;
  onQuizComplete?: (score: number, total: number) => void;
}

const QuizPlayer: React.FC<QuizPlayerProps> = ({ questions, title, description, onQuizComplete }) => {
  const [selectedAnswers, setSelectedAnswers] = useState<{[questionIndex: number]: number}>({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return; // Prevent changing answers after submission
    
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  const handleSubmitQuiz = () => {
    let correctAnswers = 0;
    questions.forEach((question, qIndex) => {
      const selectedOption = selectedAnswers[qIndex];
      if (selectedOption !== undefined && question.options[selectedOption]?.isCorrect) {
        correctAnswers++;
      }
    });
    
    setScore(correctAnswers);
    setShowResults(true);
    
    // Notify parent component of quiz completion
    if (onQuizComplete) {
      onQuizComplete(correctAnswers, questions.length);
    }
  };

  const resetQuiz = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
  };

  const getScorePercentage = () => {
    return questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
  };

  // Get performance feedback based on score
  const getPerformanceFeedback = () => {
    const percentage = getScorePercentage();
    if (percentage >= 90) return { label: "Excellent", icon: <ThumbsUp />, color: "text-green-600" };
    if (percentage >= 70) return { label: "Good", icon: <ThumbsUp />, color: "text-blue-600" };
    if (percentage >= 50) return { label: "Average", icon: <ThumbsUp />, color: "text-yellow-600" };
    return { label: "Needs Improvement", icon: <ThumbsDown />, color: "text-red-600" };
  };

  const feedback = getPerformanceFeedback();

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-2.5 transition-all duration-300 flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2 pb-1.5 border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="flex items-center min-w-0 flex-1">
          <div className="bg-green-100 p-1 rounded-lg mr-2">
            <HelpCircle className="text-green-600" size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-gray-900 truncate">{title}</h2>
          </div>
        </div>
        {showResults && (
          <button
            onClick={resetQuiz}
            className="flex items-center text-xs bg-gradient-to-r from-green-500 to-teal-500 text-white px-2 py-1 rounded-lg hover:from-green-600 hover:to-teal-600 transition-all duration-300 shadow hover:shadow-sm whitespace-nowrap flex-shrink-0"
          >
            <RotateCcw size={10} className="mr-1" />
            <span className="hidden sm:inline">Retake Quiz</span>
            <span className="sm:hidden">Retake</span>
          </button>
        )}
      </div>
      
      {questions.length > 0 ? (
        <div className="flex flex-col h-full overflow-hidden">
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2">
            <p className="text-green-800 text-xs">
              <strong>Instructions:</strong> Select the best answer for each question. 
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-1 -mr-1 pb-2">
            <div className="space-y-2">
              {questions.map((question, qIndex) => (
                <div key={qIndex} className="border border-gray-200 rounded-lg p-2 bg-white shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                    <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white text-xs font-bold px-1 py-0.5 rounded-full mr-1">
                      {qIndex + 1}
                    </span>
                    {question.questionText}
                  </h3>
                  <div className="space-y-1">
                    {question.options.map((option, oIndex) => {
                      const isSelected = selectedAnswers[qIndex] === oIndex;
                      const isCorrect = option.isCorrect;
                      const showResult = showResults;
                      
                      let optionClass = "p-1.5 rounded-lg border cursor-pointer transition-all duration-200 text-xs ";
                      if (showResult) {
                        if (isCorrect) {
                          optionClass += "bg-green-50 border-green-500";
                        } else if (isSelected && !isCorrect) {
                          optionClass += "bg-red-50 border-red-500";
                        } else {
                          optionClass += "bg-gray-50 border-gray-300";
                        }
                      } else {
                        optionClass += isSelected 
                          ? "bg-purple-50 border-purple-500" 
                          : "bg-white border-gray-300 hover:bg-gray-50";
                      }
                      
                      return (
                        <div
                          key={oIndex}
                          className={optionClass}
                          onClick={() => !showResults && handleAnswerSelect(qIndex, oIndex)}
                        >
                          <div className="flex items-start">
                            <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center mr-2 mt-0.5 flex-shrink-0 ${
                              showResult 
                                ? isCorrect 
                                  ? "bg-green-500 border-green-500" 
                                  : isSelected && !isCorrect 
                                    ? "bg-red-500 border-red-500" 
                                    : "bg-gray-300 border-gray-300"
                                : isSelected 
                                  ? "bg-purple-500 border-purple-500" 
                                  : "bg-white border-gray-400"
                            }`}>
                              {showResult && isCorrect && (
                                <CheckCircle className="text-white" size={8} />
                              )}
                              {showResult && isSelected && !isCorrect && (
                                <XCircle className="text-white" size={8} />
                              )}
                              {!showResult && isSelected && (
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                              )}
                            </div>
                            <span className="text-gray-800">{option.answertext}</span>
                          </div>
                          {showResult && isSelected && !isCorrect && (
                            <div className="mt-1 pt-1 border-t border-gray-200">
                              <p className="text-xs text-red-700">
                                <strong>Explanation:</strong> {option.explanation}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {!showResults ? (
            <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200 mt-3">
              <button
                onClick={handleSubmitQuiz}
                disabled={Object.keys(selectedAnswers).length !== questions.length}
                className={`w-full px-3 py-2.5 rounded-lg transition-all duration-300 font-medium text-sm shadow whitespace-nowrap ${
                  Object.keys(selectedAnswers).length === questions.length
                    ? "bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Submit Answers
              </button>
              <p className="text-center text-xs text-gray-500 mt-1.5">
                {Object.keys(selectedAnswers).length} of {questions.length} questions answered
              </p>
            </div>
          ) : (
            <div className="w-full bg-green-50 rounded-lg p-2 text-center border border-green-200 mt-2 flex-shrink-0">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center mb-1">
                  <span className="text-xs font-bold text-white">{getScorePercentage()}%</span>
                </div>
                <p className="text-xs font-bold text-gray-900 mb-1">
                  Score: {score}/{questions.length}
                </p>
                <div className={`flex items-center text-xs font-semibold ${feedback.color}`}>
                  {feedback.icon}
                  <span className="ml-1">{feedback.label}</span>
                </div>
                <button
                  onClick={resetQuiz}
                  className="mt-1.5 flex items-center justify-center text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white px-2 py-1 rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-300 shadow hover:shadow-sm"
                >
                  <RotateCcw size={10} className="mr-1" />
                  Retake Quiz
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 rounded-lg flex-1 flex flex-col items-center justify-center">
          <HelpCircle className="text-gray-400 mx-auto mb-2.5" size={28} />
          <h3 className="text-base font-bold text-gray-900 mb-1">No Questions Available</h3>
          <p className="text-gray-600 text-sm">This quiz doesn't have any questions yet.</p>
        </div>
      )}
    </div>
  );
};

export default QuizPlayer;